import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getAllUsers } from '../../services/users';
import { getTaskTypes } from '../../services/taskTypes';
import './TaskHistoryTab.css';

const CALENDAR_TABLE = 'calendar_events';

async function fetchCalendarEventsByClient(clientId, options = {}) {
  let query = supabase
    .from(CALENDAR_TABLE)
    .select(`
      *,
      clients:client_id (
        id,
        name
      )
    `)
    .eq('client_id', clientId)
    .order('start_date', { ascending: false });
  if (options.status) query = query.eq('status', options.status);
  if (options.taskType) query = query.eq('task_type', options.taskType);
  if (options.startDate) query = query.gte('start_date', options.startDate.toISOString());
  if (options.endDate) query = query.lte('start_date', options.endDate.toISOString());
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'started', label: 'Started' },
  { value: 'completed', label: 'Completed' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'short' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

export default function TaskHistoryTab({ clientId }) {
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    taskType: '',
    startDate: '',
    endDate: '',
  });
  const [taskTypes, setTaskTypes] = useState([]);

  const userMap = React.useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.id] = u.user_metadata?.full_name || u.email || '—'; });
    return m;
  }, [users]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadTaskTypes = useCallback(async () => {
    try {
      const data = await getTaskTypes(false);
      setTaskTypes(data || []);
    } catch (error) {
      console.error('Error loading task types:', error);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const options = {};
      if (filters.status) options.status = filters.status;
      if (filters.taskType) options.taskType = filters.taskType;
      if (filters.startDate) options.startDate = new Date(filters.startDate + 'T00:00:00');
      if (filters.endDate) options.endDate = new Date(filters.endDate + 'T23:59:59');
      const data = await fetchCalendarEventsByClient(clientId, options);
      setEvents(data);
    } catch (error) {
      console.error('Error loading task history:', error);
      alert('Failed to load task history: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, filters.status, filters.taskType, filters.startDate, filters.endDate]);

  useEffect(() => {
    loadUsers();
    loadTaskTypes();
  }, [loadUsers, loadTaskTypes]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="task-history-tab">
      <div className="task-history-tab-filters">
        <div className="filter-group">
          <label htmlFor="th-status">Status</label>
          <select
            id="th-status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="th-task-type">Type</label>
          <select
            id="th-task-type"
            value={filters.taskType}
            onChange={(e) => handleFilterChange('taskType', e.target.value)}
          >
            <option value="">All types</option>
            {taskTypes.length > 0
              ? taskTypes.map((tt) => (
                  <option key={tt.id} value={tt.code}>
                    {tt.name}
                  </option>
                ))
              : [
                  { code: 'general', name: 'General' },
                  { code: 'call', name: 'Call' },
                  { code: 'follow_up', name: 'Follow-Up' },
                  { code: 'estimate_follow_up', name: 'Estimate Follow-Up' },
                  { code: 'invoice_follow_up', name: 'Invoice Follow-Up' },
                  { code: 'site_visit', name: 'Site Visit' },
                ].map((tt) => (
                  <option key={tt.code} value={tt.code}>
                    {tt.name}
                  </option>
                ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="th-start">From date</label>
          <input
            id="th-start"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="th-end">To date</label>
          <input
            id="th-end"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="task-history-tab-loading">
          <div className="loading-spinner"></div>
          <p>Loading tasks…</p>
        </div>
      ) : events.length === 0 ? (
        <div className="task-history-tab-empty">
          <p>No tasks match the current filters.</p>
        </div>
      ) : (
        <div className="task-history-tab-list">
          {events.map((ev) => (
            <div key={ev.id} className="task-history-tab-card">
              <div className="task-history-tab-card-header">
                <span className="task-history-tab-title">{ev.title || 'Untitled task'}</span>
                <span className={`status-badge status-${ev.status || 'scheduled'}`}>
                  {ev.status || 'scheduled'}
                </span>
              </div>
              <div className="task-history-tab-card-meta">
                <span>
                  {ev.all_day ? formatDate(ev.start_date) : formatDateTime(ev.start_date)}
                </span>
                <span className="task-type">{(ev.task_type || 'general').replace(/_/g, ' ')}</span>
                {ev.assigned_to && (
                  <span>Assigned: {userMap[ev.assigned_to] || '—'}</span>
                )}
              </div>
              {ev.description && (
                <p className="task-history-tab-description">{ev.description}</p>
              )}
              <div className="task-history-tab-links">
                <Link to={`/clients/${clientId}?tab=order-form`} className="task-history-tab-link">
                  Order form
                </Link>
                {ev.completion_note?.trim() && (
                  <>
                    <span className="task-history-tab-link-sep">·</span>
                    <Link to={`/clients/${clientId}?tab=order-form`} className="task-history-tab-link">
                      View note
                    </Link>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
